<template>
  <VaModal
    v-bind="modalAttrs"
    :model-value="modelValue"
    :size="size"
    mobile-fullscreen
    hide-default-actions
    close-button
    :stateful="stateful"
    :before-cancel="beforeCancel"
    :max-height="maxHeight"
    @update:modelValue="onUpdateModelValue"
  >
    <div class="app-dialog" :class="{ 'app-dialog--fit-content': fitContent }">
      <header v-if="title || $slots.header" class="app-dialog__header">
        <slot name="header">
          <h2 class="va-h5 m-0">{{ title }}</h2>
          <p v-if="subtitle" class="app-dialog__subtitle">{{ subtitle }}</p>
        </slot>
      </header>

      <div class="app-dialog__body">
        <slot :cancel="handleCancel" :ok="handleOk" />
      </div>

      <AppDialogActions
        v-if="hasActions"
        :cancel-label="cancelLabel"
        :primary-label="primaryLabel"
        :show-cancel="showCancel"
        :show-primary="showPrimary"
        :loading="loading"
        :primary-disabled="primaryDisabled"
        :primary-preset="primaryPreset"
        :primary-color="primaryColor"
        @cancel="handleCancel"
        @primary="emit('primary')"
      >
        <template v-if="$slots['actions-secondary']" #secondary>
          <slot name="actions-secondary" />
        </template>
        <template v-if="$slots['actions-primary']" #primary>
          <slot name="actions-primary" />
        </template>
      </AppDialogActions>

      <div v-if="hasFooter" class="app-dialog__footer">
        <slot name="footer" />
      </div>
    </div>
  </VaModal>
</template>

<script setup lang="ts">
import { computed, useAttrs, useSlots, Comment } from 'vue'
import { VaModal } from 'vuestic-ui'
import AppDialogActions from './AppDialogActions.vue'
import './dialog-theme.css'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    title?: string
    subtitle?: string
    size?: 'small' | 'medium' | 'large'
    stateful?: boolean
    beforeCancel?: (hide: () => void) => void | Promise<void>
    maxHeight?: string
    loading?: boolean
    cancelLabel?: string
    primaryLabel?: string
    showCancel?: boolean
    showPrimary?: boolean
    primaryDisabled?: boolean
    primaryPreset?: string
    primaryColor?: string
    fitContent?: boolean
  }>(),
  {
    title: undefined,
    subtitle: undefined,
    size: 'small',
    stateful: false,
    beforeCancel: undefined,
    maxHeight: 'calc(100% - 2rem)',
    loading: false,
    cancelLabel: 'Cancel',
    primaryLabel: 'Confirm',
    showCancel: false,
    showPrimary: false,
    primaryDisabled: false,
    primaryPreset: 'primary',
    primaryColor: undefined,
    fitContent: false,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  close: []
  primary: []
  ok: []
}>()

const attrs = useAttrs()
const slots = useSlots()

const modalAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs
  return rest
})

const hasActions = computed(
  () => props.showCancel || props.showPrimary || !!slots['actions-secondary'] || !!slots['actions-primary'],
)

const hasFooter = computed(() => {
  const footer = slots.footer?.()
  return !!footer?.some((node) => node.type !== Comment)
})

function onUpdateModelValue(value: boolean) {
  emit('update:modelValue', value)
  if (!value) {
    emit('close')
  }
}

function handleCancel() {
  emit('update:modelValue', false)
  emit('close')
}

function handleOk() {
  emit('ok')
  emit('update:modelValue', false)
}
</script>
